import { useAuth } from '@/contexts/AuthContext';
import { menuItems } from '@/components/sidebar/menuItems';
import { useEnhancedPermissions } from './useEnhancedPermissions';

export function usePermissions() {
  const { user } = useAuth();
  const enhancedPermissions = useEnhancedPermissions();

  const hasPermission = (permission: string): boolean => {
    return enhancedPermissions.hasPermission(permission);
  };

  const canAccessRoute = (route: string): boolean => {
    // تحقق أساسي من وجود المستخدم
    if (!user) {
      return false;
    }

    // السماح بالوصول للصفحة الرئيسية لجميع المستخدمين المسجلين
    if (route === '/' || route === '/dashboard') {
      return true;
    }

    return enhancedPermissions.canAccessRoute(route);
  };

  const getDepartmentPermissions = () => {
    return enhancedPermissions.getAllAvailablePermissions();
  };

  const getAllAvailablePermissions = () => {
    return enhancedPermissions.getAllAvailablePermissions();
  };

  const getFilteredMenuItems = () => {
    // إذا لم يكن هناك مستخدم، لا تعرض أي عناصر
    if (!user) {
      return [];
    }
    
    // For system admins, return all menu items immediately
    if (user?.employeeData?.email === 'klidmorre@gmail.com' ||
        user?.permissions === 'ALL' || 
        user?.username === 'مدير النظام الرئيسي' || 
        user?.role === 'مدير النظام الرئيسي' ||
        user?.username === 'مدير النظام العام' ||
        user?.role === 'مدير النظام العام') {
      console.log('Admin access: showing all menu items');
      return menuItems;
    }
    
    // التحقق من وجود صلاحيات صالحة
    if (!user?.permissions || (Array.isArray(user.permissions) && user.permissions.length === 0)) {
      return menuItems.filter(item => item.href === '/dashboard' || item.title === 'الرئيسية');
    }
    
    // إذا لم تكن الصلاحيات في صورة array، عرض الأساسيات فقط
    if (!Array.isArray(user.permissions)) {
      return menuItems.filter(item => item.href === '/dashboard' || item.title === 'الرئيسية');
    }
    
    // فلترة العناصر بناءً على الصلاحيات الفعلية
    const filteredItems = menuItems.filter(item => {
      // التحقق من وجود صلاحية لهذا المسار أو أي مسار فرعي
      if (item.href && item.href !== '#') {
        const routePermission = enhancedPermissions.MENU_PERMISSIONS_MAP[item.href as keyof typeof enhancedPermissions.MENU_PERMISSIONS_MAP];
        if (routePermission) {
          return hasPermission(routePermission.permission);
        }
      }
      
      // للعناصر التي تحتوي على عناصر فرعية، التحقق من وجود صلاحية لأي عنصر فرعي
      if (item.subItems && item.subItems.length > 0) {
        const hasSubItemPermission = item.subItems.some(subItem => {
          if (subItem.href && subItem.href !== '#') {
            const subRoutePermission = enhancedPermissions.MENU_PERMISSIONS_MAP[subItem.href as keyof typeof enhancedPermissions.MENU_PERMISSIONS_MAP];
            if (subRoutePermission) {
              return hasPermission(subRoutePermission.permission);
            }
          }
          return false;
        });
        
        if (hasSubItemPermission) {
          return true;
        }
      }
      
      // التحقق من الصلاحيات المباشرة للعنصر
      if (item.permission) {
        return hasPermission(item.permission);
      }
      
      return false;
    }).map(item => {
      // فلترة العناصر الفرعية للعناصر المضمنة
      if (item.subItems && item.subItems.length > 0) {
        const filteredSubItems = item.subItems.filter(subItem => {
          if (subItem.href && subItem.href !== '#') {
            const subRoutePermission = enhancedPermissions.MENU_PERMISSIONS_MAP[subItem.href as keyof typeof enhancedPermissions.MENU_PERMISSIONS_MAP];
            if (subRoutePermission) {
              return hasPermission(subRoutePermission.permission);
            }
          }
          
          if (subItem.permission) {
            return hasPermission(subItem.permission);
          }
          
          return false;
        });
        
        return {
          ...item,
          subItems: filteredSubItems
        };
      }
      
      return item;
    });
    
    return filteredItems;
  };

  return {
    hasPermission,
    canAccessRoute,
    getDepartmentPermissions,
    getAllAvailablePermissions,
    getFilteredMenuItems,
    userPermissions: user?.permissions || [],
    isSystemAdmin: user?.employeeData?.email === 'klidmorre@gmail.com' ||
                   user?.permissions === 'ALL' || 
                   user?.username === 'مدير النظام الرئيسي' ||
                   user?.role === 'مدير النظام الرئيسي' ||
                   user?.username === 'مدير النظام العام' ||
                   user?.role === 'مدير النظام العام'
  };
}
